import { getMessageList } from '@/lib/locale';
import { m } from '@/locale/paraglide/messages';
import { clientEnv } from '@/env/client';
import { SITE } from '@/config/site';
import { REGISTER_GIFT_CREDITS } from '@/config/credit-economics';
import {
  getCreditPackages,
  LIFETIME_PRICE_CENTS,
  legacySubscriptionPlans,
  type PricingPlan,
} from '@/config/pricing-config';
import { INLINE_CREDIT_PRICE_PREFIX } from '@/payment/constants';
import { PlanIntervals, PaymentTypes, type PricePlan } from '@/payment/types';
import type { WebsiteConfig } from '../types';
import {
  DEFAULT_ALLOWED_TYPES,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_USER_FILES_FOLDER,
} from '@/storage/constants';

// Payment provider controlled by env var: 'stripe' | 'creem' | '' (empty means disabled)
const paymentProvider = clientEnv.VITE_PAYMENT_PROVIDER;
const isPaymentEnabled = paymentProvider !== '';
const isStripePayment = paymentProvider === 'stripe';
const isCreemPayment = paymentProvider === 'creem';
// Resolve price/product IDs based on the active payment provider
const priceIds = isPaymentEnabled
  ? {
      starterMonthly: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_STARTER_MONTHLY ?? '')
        : '',
      starterYearly: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_STARTER_YEARLY ?? '')
        : '',
      growthMonthly: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_GROWTH_MONTHLY ?? '')
        : '',
      growthYearly: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_GROWTH_YEARLY ?? '')
        : '',
      proMonthly: isCreemPayment
        ? (clientEnv.VITE_CREEM_PRODUCT_PRO_MONTHLY ?? '')
        : (clientEnv.VITE_STRIPE_PRICE_PRO_MONTHLY ?? ''),
      proYearly: isCreemPayment
        ? (clientEnv.VITE_CREEM_PRODUCT_PRO_YEARLY ?? '')
        : (clientEnv.VITE_STRIPE_PRICE_PRO_YEARLY ?? ''),
      lifetime: isCreemPayment
        ? (clientEnv.VITE_CREEM_PRODUCT_LIFETIME ?? '')
        : (clientEnv.VITE_STRIPE_PRICE_LIFETIME ?? ''),
      creditBasic: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_BASIC ?? '')
        : '',
      creditStandard: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_STANDARD ?? '')
        : '',
      creditPremium: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_PREMIUM ?? '')
        : '',
      creditEnterprise: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_ENTERPRISE ?? '')
        : '',
      creditCreatorPack: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_CREATOR_PACK ?? '')
        : '',
      creditCampaignPack: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_CAMPAIGN_PACK ?? '')
        : '',
      creditPopularPack: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_POPULAR_PACK ?? '')
        : '',
      creditStudioPack: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_STUDIO_PACK ?? '')
        : '',
      creditScalePack: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREDIT_SCALE_PACK ?? '')
        : '',
      starterDrop: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_STARTER_DROP ?? '')
        : '',
      creatorStack: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_CREATOR_STACK ?? '')
        : '',
      studioVault: isStripePayment
        ? (clientEnv.VITE_STRIPE_PRICE_STUDIO_VAULT ?? '')
        : '',
    }
  : {
      starterMonthly: '',
      starterYearly: '',
      growthMonthly: '',
      growthYearly: '',
      proMonthly: '',
      proYearly: '',
      lifetime: '',
      creditBasic: '',
      creditStandard: '',
      creditPremium: '',
      creditEnterprise: '',
      creditCreatorPack: '',
      creditCampaignPack: '',
      creditPopularPack: '',
      creditStudioPack: '',
      creditScalePack: '',
      starterDrop: '',
      creatorStack: '',
      studioVault: '',
    };

const creditPriceIdsByPackage: Record<string, string> = {
  'starter-drop': priceIds.starterDrop,
  'creator-stack': priceIds.creatorStack,
  'studio-vault': priceIds.studioVault,
};
// Old Stripe price env vars stay readable so existing checkouts can resolve.
void [
  priceIds.creditBasic,
  priceIds.creditStandard,
  priceIds.creditPremium,
  priceIds.creditEnterprise,
  priceIds.creditCreatorPack,
  priceIds.creditCampaignPack,
  priceIds.creditPopularPack,
  priceIds.creditStudioPack,
  priceIds.creditScalePack,
];

const creditPackages = Object.fromEntries(
  getCreditPackages().map((pkg) => [
    pkg.id,
    {
      ...pkg,
      price: {
        ...pkg.price,
        // Stripe can charge packs without a pre-created Price via price_data.
        priceId:
          creditPriceIdsByPackage[pkg.id] ||
          (isStripePayment
            ? `${INLINE_CREDIT_PRICE_PREFIX}${pkg.id}`
            : pkg.price.priceId),
      },
    },
  ])
);

const subscriptionPriceIdsByPlan: Record<
  string,
  { monthly: string; yearly: string }
> = {
  starter: {
    monthly: priceIds.starterMonthly,
    yearly: priceIds.starterYearly,
  },
  growth: {
    monthly: priceIds.growthMonthly,
    yearly: priceIds.growthYearly,
  },
  pro: {
    monthly: priceIds.proMonthly,
    yearly: priceIds.proYearly,
  },
};

function yearlyAmount(plan: PricingPlan): number {
  return Math.round((plan.priceYearly ?? plan.price) * 12 * 100);
}

function toPaymentPlan(plan: PricingPlan): PricePlan {
  const ids = subscriptionPriceIdsByPlan[plan.id] ?? {
    monthly: '',
    yearly: '',
  };

  return {
    id: plan.id,
    name: plan.name,
    description: plan.description,
    features: plan.features.map((feature) => feature.text),
    limits: [plan.credits],
    prices: [
      {
        type: PaymentTypes.SUBSCRIPTION,
        priceId: ids.monthly,
        amount: Math.round(plan.price * 100),
        currency: 'USD',
        interval: PlanIntervals.MONTH,
        allowPromotionCode: true,
      },
      {
        type: PaymentTypes.SUBSCRIPTION,
        priceId: ids.yearly,
        amount: yearlyAmount(plan),
        currency: 'USD',
        interval: PlanIntervals.YEAR,
        allowPromotionCode: true,
      },
    ],
    isFree: false,
    isLifetime: false,
    popular: plan.isPopular,
  };
}

const subscriptionPaymentPlans = Object.fromEntries(
  legacySubscriptionPlans.map((plan) => [
    plan.id,
    { ...toPaymentPlan(plan), disabled: true },
  ])
);

/**
 * Website config
 */
export const websiteConfig: WebsiteConfig = {
  ui: {
    mode: {
      defaultMode: 'dark',
      enableSwitch: true,
    },
  },
  metadata: {
    get name() {
      return m.site_name();
    },
    get title() {
      return m.site_title();
    },
    get description() {
      return m.site_description();
    },
    images: {
      ogImage: '/og.jpeg',
      logoLight: '/logo.png',
      logoDark: '/logo-dark.png',
    },
  },
  social: {
    // github: 'https://github.com/MkFastHQ',
    // twitter: 'https://x.com/TanStarter',
    // youtube: 'https://www.youtube.com/@TanStarter',
  },
  auth: {
    enable: true,
    enableGoogleLogin: true,
    enableCredentialLogin: false,
    enableDeleteAccount: true,
  },
  blog: {
    enable: true,
    paginationSize: 6,
  },
  mail: {
    enable: true,
    provider: 'cloudflare',
    fromEmail: `${SITE.name} <${SITE.supportEmail}>`,
    supportEmail: `${SITE.name} <${SITE.supportEmail}>`,
  },
  newsletter: {
    enable: false,
    provider: 'resend',
    autoSubscribeAfterSignUp: true,
  },
  notification: {
    enable: true,
    provider: 'discord',
  },
  storage: {
    enable: true,
    provider: 'r2',
    maxFileSize: DEFAULT_MAX_FILE_SIZE,
    allowedTypes: DEFAULT_ALLOWED_TYPES,
    userFilesFolder: DEFAULT_USER_FILES_FOLDER,
  },
  // Subscription plans and credit packages share the same checkout provider.
  payment: {
    enable: isPaymentEnabled,
    provider: isPaymentEnabled ? paymentProvider : undefined,
    price: {
      enable: isPaymentEnabled,
      plans: {
        free: {
          id: 'free',
          prices: [],
          isFree: true,
          isLifetime: false,
          get name() {
            return m.pricing_plans_free_name();
          },
          get description() {
            return m.pricing_plans_free_description();
          },
          get features() {
            return [...getMessageList(m.pricing_plans_free_features())];
          },
          get limits() {
            return [...getMessageList(m.pricing_plans_free_limits())];
          },
        },
        ...subscriptionPaymentPlans,
        lifetime: {
          id: 'lifetime',
          prices: [
            {
              type: 'one_time',
              priceId: priceIds.lifetime,
              amount: LIFETIME_PRICE_CENTS,
              currency: 'USD',
              allowPromotionCode: true,
              disabled: true,
            },
          ],
          isFree: false,
          isLifetime: true,
          disabled: true,
          get name() {
            return m.pricing_plans_lifetime_name();
          },
          get description() {
            return m.pricing_plans_lifetime_description();
          },
          get features() {
            return [...getMessageList(m.pricing_plans_lifetime_features())];
          },
          get limits() {
            return [...getMessageList(m.pricing_plans_lifetime_limits())];
          },
        },
      },
    },
  },
  // Stripe-only credit packages; the free tier is a non-purchasable placeholder.
  credits: {
    enable: isPaymentEnabled && isStripePayment,
    registerGiftCredits: {
      enable: REGISTER_GIFT_CREDITS > 0,
      amount: REGISTER_GIFT_CREDITS,
    },
    packages: creditPackages,
  },
};
